import React, { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Card } from '../ui';
import { TRANSACTION_RANGES } from './useCashMovementData';

const TICK_STYLE = { fontSize: 11, fill: '#94a3b8' };
const DAY_MS = 24 * 60 * 60 * 1000;
const VOID_STATUSES = new Set(['REJECTED', 'CANCELLED', 'EXPIRED']);

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-lg border border-ink-200 bg-white shadow-popover px-3.5 py-2.5">
      <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{point.label}</p>
      <p className="mt-1 text-sm font-semibold text-ink-900 tnum">{point.orders} orders</p>
      <div className="mt-1.5 space-y-0.5">
        <p className="text-xs text-ink-500 tnum">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent-500 mr-1.5" />
          Buy {point.buyOrders}
        </p>
        <p className="text-xs text-ink-500 tnum">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-ink-300 mr-1.5" />
          Sell {point.sellOrders}
        </p>
      </div>
    </div>
  );
};

function buildOrdersSeries(orders, days) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const buckets = new Map();
  for (let i = days - 1; i >= 0; i -= 1) {
    const date = new Date(today.getTime() - i * DAY_MS);
    const key = date.toISOString().slice(0, 10);
    buckets.set(key, {
      date: key,
      label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      buyOrders: 0,
      sellOrders: 0,
      orders: 0,
    });
  }
  orders.forEach((o) => {
    if (VOID_STATUSES.has(o.status)) return;
    const key = new Date(o.createdAt).toISOString().slice(0, 10);
    const bucket = buckets.get(key);
    if (!bucket) return;
    if (o.side === 'BUY') {
      bucket.buyOrders += 1;
    } else {
      bucket.sellOrders += 1;
    }
    bucket.orders += 1;
  });
  return Array.from(buckets.values());
}

function computeSeriesDelta(series) {
  const half = Math.floor(series.length / 2);
  if (half === 0) return 0;
  const firstHalf = series.slice(0, half).reduce((s, p) => s + p.orders, 0);
  const secondHalf = series.slice(half).reduce((s, p) => s + p.orders, 0);
  if (firstHalf === 0) return 0;
  return ((secondHalf - firstHalf) / firstHalf) * 100;
}

const OrdersActivityChart = ({ orders, loading }) => {
  const [range, setRange] = useState('30d');
  const rangeDef = TRANSACTION_RANGES.find((r) => r.value === range) || TRANSACTION_RANGES[1];

  const { series, totalOrders, buyPct, sellPct, deltaPct } = useMemo(() => {
    if (!orders || orders.length === 0) {
      return { series: [], totalOrders: 0, buyPct: 0, sellPct: 0, deltaPct: 0 };
    }
    const s = buildOrdersSeries(orders, rangeDef.days);
    const total = s.reduce((sum, p) => sum + p.orders, 0);
    const buy = s.reduce((sum, p) => sum + p.buyOrders, 0);
    const sell = s.reduce((sum, p) => sum + p.sellOrders, 0);
    return {
      series: s,
      totalOrders: total,
      buyPct: total > 0 ? (buy / total) * 100 : 0,
      sellPct: total > 0 ? (sell / total) * 100 : 0,
      deltaPct: computeSeriesDelta(s),
    };
  }, [orders, rangeDef.days]);

  const tickInterval = rangeDef.value === '7d' ? 0 : rangeDef.value === '30d' ? 4 : 12;
  const isUp = deltaPct >= 0;
  const isEmpty = !loading && (!orders || orders.length === 0);

  return (
    <Card>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-medium text-ink-500 uppercase tracking-wider">Trading activity</p>
          <div className="mt-1.5 flex items-baseline gap-2.5">
            {loading ? (
              <span className="text-3xl font-semibold tracking-tight text-ink-300 tnum animate-pulse">—</span>
            ) : (
              <p className="text-3xl font-semibold tracking-tight text-ink-900 tnum">{totalOrders.toLocaleString('en-US')}</p>
            )}
            {!loading && totalOrders > 0 && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded ${
                  isUp ? 'text-success-700 bg-success-50' : 'text-danger-700 bg-danger-50'
                }`}
              >
                {isUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {Math.abs(deltaPct).toFixed(1)}%
              </span>
            )}
          </div>
          {!loading && totalOrders > 0 && (
            <p className="mt-1 text-xs text-ink-500 tnum">
              Orders placed · Buy {buyPct.toFixed(0)}% · Sell {sellPct.toFixed(0)}%
            </p>
          )}
        </div>

        <div className="inline-flex rounded-md border border-ink-200 bg-ink-50 p-0.5">
          {TRANSACTION_RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`px-3 py-1 rounded-[5px] text-xs font-medium transition-colors ${
                range === r.value ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-500 hover:text-ink-700'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 h-56">
        {isEmpty ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-ink-400">No trading activity yet</p>
          </div>
        ) : loading ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-ink-300 animate-pulse">Loading…</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="#4f46e5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={TICK_STYLE} interval={tickInterval} minTickGap={24} />
              <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }} />
              <Area
                type="monotone"
                dataKey="orders"
                stroke="#4f46e5"
                strokeWidth={2}
                fill="url(#ordersGradient)"
                dot={false}
                activeDot={{ r: 3.5, fill: '#4f46e5', strokeWidth: 0 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
};

export default OrdersActivityChart;
